import { CubeLut, sampleLut, Vec3 } from "./lut";

export interface SpectraFitRecipe {
  version: "SpectraFit v1";
  exposure: number;
  contrast: number;
  gamma: Vec3;
  saturation: number;
  temperature: number;
  tint: number;
  rgbBias: Vec3;
  matrix: [Vec3, Vec3, Vec3];
  fitScore: number;
  meanRgbError: number;
  maxRgbError: number;
}

export type SpectraFitTune =
  | { field: "exposure" | "contrast" | "saturation" | "temperature" | "tint"; value: number }
  | { field: "rgbBias"; index: 0 | 1 | 2; value: number };

interface TrainingPair {
  input: Vec3;
  output: Vec3;
}

const saturationLuma: Vec3 = [0.2126, 0.7152, 0.0722];

export function sampleTrainingPairs(lut: CubeLut, gridSize = Math.min(lut.dimension, 17)): TrainingPair[] {
  const size = Math.max(2, gridSize);
  const pairs: TrainingPair[] = [];

  for (let z = 0; z < size; z += 1) {
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        const input: Vec3 = [
          x / (size - 1),
          y / (size - 1),
          z / (size - 1)
        ];
        pairs.push({ input, output: sampleLut(input, lut) });
      }
    }
  }

  return pairs;
}

export function fitMatrixRecipe(lut: CubeLut): SpectraFitRecipe {
  const pairs = sampleTrainingPairs(lut);
  const red = solveAffineChannel(pairs, 0);
  const green = solveAffineChannel(pairs, 1);
  const blue = solveAffineChannel(pairs, 2);

  const matrix: [Vec3, Vec3, Vec3] = [
    [red[0], red[1], red[2]],
    [green[0], green[1], green[2]],
    [blue[0], blue[1], blue[2]]
  ];
  const bias: Vec3 = [red[3], green[3], blue[3]];

  let totalError = 0;
  let maxRgbError = 0;
  for (const pair of pairs) {
    const predicted = applyAffine01(pair.input, matrix, bias);
    const error = rgbError255(predicted, pair.output);
    totalError += error;
    maxRgbError = Math.max(maxRgbError, error);
  }

  const meanRgbError = totalError / pairs.length;
  const fitScore = clamp(100 - meanRgbError * 2, 0, 100);

  return {
    version: "SpectraFit v1",
    exposure: estimateExposure(bias),
    contrast: estimateContrast(matrix),
    gamma: [1, 1, 1],
    saturation: estimateSaturation(matrix),
    temperature: estimateTemperature(matrix, bias),
    tint: estimateTint(matrix, bias),
    rgbBias: [bias[0] * 255, bias[1] * 255, bias[2] * 255],
    matrix,
    fitScore,
    meanRgbError,
    maxRgbError
  };
}

export function applySpectraFitRecipe(rgb: Vec3, recipe: SpectraFitRecipe): Vec3 {
  const input: Vec3 = [rgb[0] / 255, rgb[1] / 255, rgb[2] / 255];
  const bias: Vec3 = [
    recipe.rgbBias[0] / 255,
    recipe.rgbBias[1] / 255,
    recipe.rgbBias[2] / 255
  ];
  const output = applyAffine01(input, recipe.matrix, bias);
  return [
    Math.round(output[0] * 255),
    Math.round(output[1] * 255),
    Math.round(output[2] * 255)
  ];
}

export function applySpectraFitRecipeToImageData(source: ImageData, recipe: SpectraFitRecipe): ImageData {
  const output = new ImageData(new Uint8ClampedArray(source.data), source.width, source.height);
  for (let index = 0; index < output.data.length; index += 4) {
    const [r, g, b] = applySpectraFitRecipe(
      [output.data[index], output.data[index + 1], output.data[index + 2]],
      recipe
    );
    output.data[index] = r;
    output.data[index + 1] = g;
    output.data[index + 2] = b;
  }
  return output;
}

export function tuneSpectraFitRecipe(recipe: SpectraFitRecipe, tune: SpectraFitTune): SpectraFitRecipe {
  const next: SpectraFitRecipe = {
    ...recipe,
    gamma: [...recipe.gamma],
    rgbBias: [...recipe.rgbBias],
    matrix: recipe.matrix.map((row) => [...row] as Vec3) as [Vec3, Vec3, Vec3]
  };

  if (tune.field === "rgbBias") {
    next.rgbBias[tune.index] = tune.value;
    return next;
  }

  const previous = recipe[tune.field];
  const delta = tune.value - previous;
  next[tune.field] = tune.value;

  if (tune.field === "exposure") {
    next.rgbBias = next.rgbBias.map((value) => value + delta) as Vec3;
  }

  if (tune.field === "contrast") {
    const shift = delta / 100;
    next.matrix[0][0] += shift;
    next.matrix[1][1] += shift;
    next.matrix[2][2] += shift;
  }

  if (tune.field === "saturation") {
    const factor = Math.max(0, 1 + delta / 100);
    next.matrix = next.matrix.map((row) => {
      return row.map((value, index) => saturationLuma[index] * (1 - factor) + value * factor) as Vec3;
    }) as [Vec3, Vec3, Vec3];
  }

  if (tune.field === "temperature") {
    next.rgbBias[0] += delta * 0.5;
    next.rgbBias[2] -= delta * 0.5;
  }

  if (tune.field === "tint") {
    next.rgbBias[1] += delta * 0.5;
  }

  return next;
}

export function formatSpectraFitRecipe(recipe: SpectraFitRecipe): string {
  return [
    recipe.version,
    `Exposure：${formatSigned(recipe.exposure)}`,
    `Contrast：${formatSigned(recipe.contrast)}`,
    `Gamma R：${recipe.gamma[0].toFixed(2)}`,
    `Gamma G：${recipe.gamma[1].toFixed(2)}`,
    `Gamma B：${recipe.gamma[2].toFixed(2)}`,
    `Saturation：${formatSigned(recipe.saturation)}`,
    `Temperature：${formatSigned(recipe.temperature)}`,
    `Tint：${formatSigned(recipe.tint)}`,
    `RGB Bias：${recipe.rgbBias.map((value) => formatSigned(value)).join(" ")}`,
    "Matrix：",
    ...recipe.matrix.map((row) => row.map((value) => value.toFixed(4)).join(" ")),
    `Fit Score：${recipe.fitScore.toFixed(0)}`,
    `Mean RGB Error：${recipe.meanRgbError.toFixed(2)}`,
    `Max RGB Error：${recipe.maxRgbError.toFixed(2)}`
  ].join("\n");
}

export function parseSpectraFitRecipe(text: string): SpectraFitRecipe {
  const normalized = text.trim();
  if (!normalized.includes("SpectraFit v1")) {
    throw new Error("Missing SpectraFit v1 header.");
  }

  const lines = normalized
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const matrixIndex = lines.findIndex((line) => normalizeKey(line).startsWith("matrix"));
  if (matrixIndex < 0) {
    throw new Error("Missing Matrix block.");
  }

  const matrixLines = lines.slice(matrixIndex + 1, matrixIndex + 4);
  if (matrixLines.length !== 3 || matrixLines.some((line) => parseNumberList(line).length !== 3)) {
    throw new Error("Matrix block must contain three rows of three numbers.");
  }

  const matrix = matrixLines.map((line) => parseNumberList(line) as Vec3) as [Vec3, Vec3, Vec3];
  const rgbBias = parseNumberList(readValue(lines, "rgb bias") ?? "0 0 0");
  if (rgbBias.length !== 3) {
    throw new Error("RGB Bias must contain three numbers.");
  }

  return {
    version: "SpectraFit v1",
    exposure: parseOptionalNumber(lines, "exposure", 0),
    contrast: parseOptionalNumber(lines, "contrast", 0),
    gamma: [
      parseOptionalNumber(lines, "gamma r", 1),
      parseOptionalNumber(lines, "gamma g", 1),
      parseOptionalNumber(lines, "gamma b", 1)
    ],
    saturation: parseOptionalNumber(lines, "saturation", 0),
    temperature: parseOptionalNumber(lines, "temperature", 0),
    tint: parseOptionalNumber(lines, "tint", 0),
    rgbBias: rgbBias as Vec3,
    matrix,
    fitScore: parseOptionalNumber(lines, "fit score", 0),
    meanRgbError: parseOptionalNumber(lines, "mean rgb error", 0),
    maxRgbError: parseOptionalNumber(lines, "max rgb error", 0)
  };
}

function solveAffineChannel(pairs: TrainingPair[], channel: 0 | 1 | 2): [number, number, number, number] {
  const normal = createMatrix(4, 4);
  const rhs = [0, 0, 0, 0];

  for (const pair of pairs) {
    const row = [pair.input[0], pair.input[1], pair.input[2], 1];
    for (let r = 0; r < 4; r += 1) {
      rhs[r] += row[r] * pair.output[channel];
      for (let c = 0; c < 4; c += 1) {
        normal[r][c] += row[r] * row[c];
      }
    }
  }

  return solveLinear4(normal, rhs);
}

function solveLinear4(matrix: number[][], vector: number[]): [number, number, number, number] {
  const augmented = matrix.map((row, index) => [...row, vector[index]]);

  for (let pivot = 0; pivot < 4; pivot += 1) {
    let bestRow = pivot;
    for (let row = pivot + 1; row < 4; row += 1) {
      if (Math.abs(augmented[row][pivot]) > Math.abs(augmented[bestRow][pivot])) {
        bestRow = row;
      }
    }

    if (bestRow !== pivot) {
      [augmented[pivot], augmented[bestRow]] = [augmented[bestRow], augmented[pivot]];
    }

    const pivotValue = augmented[pivot][pivot] || 1e-12;
    for (let col = pivot; col < 5; col += 1) {
      augmented[pivot][col] /= pivotValue;
    }

    for (let row = 0; row < 4; row += 1) {
      if (row === pivot) continue;
      const factor = augmented[row][pivot];
      for (let col = pivot; col < 5; col += 1) {
        augmented[row][col] -= factor * augmented[pivot][col];
      }
    }
  }

  return [augmented[0][4], augmented[1][4], augmented[2][4], augmented[3][4]];
}

function applyAffine01(input: Vec3, matrix: [Vec3, Vec3, Vec3], bias: Vec3): Vec3 {
  return [
    clamp(matrix[0][0] * input[0] + matrix[0][1] * input[1] + matrix[0][2] * input[2] + bias[0], 0, 1),
    clamp(matrix[1][0] * input[0] + matrix[1][1] * input[1] + matrix[1][2] * input[2] + bias[1], 0, 1),
    clamp(matrix[2][0] * input[0] + matrix[2][1] * input[1] + matrix[2][2] * input[2] + bias[2], 0, 1)
  ];
}

function rgbError255(a: Vec3, b: Vec3): number {
  const dr = (a[0] - b[0]) * 255;
  const dg = (a[1] - b[1]) * 255;
  const db = (a[2] - b[2]) * 255;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

function estimateExposure(bias: Vec3): number {
  return average(bias) * 100;
}

function estimateContrast(matrix: [Vec3, Vec3, Vec3]): number {
  const diagonalAverage = (matrix[0][0] + matrix[1][1] + matrix[2][2]) / 3;
  return (diagonalAverage - 1) * 100;
}

function estimateSaturation(matrix: [Vec3, Vec3, Vec3]): number {
  const diagonalAverage = (matrix[0][0] + matrix[1][1] + matrix[2][2]) / 3;
  const rowSumAverage = matrix.reduce((sum, row) => sum + row[0] + row[1] + row[2], 0) / 3;
  return (diagonalAverage - rowSumAverage / 3) * 100;
}

function estimateTemperature(matrix: [Vec3, Vec3, Vec3], bias: Vec3): number {
  return ((matrix[0][0] + bias[0]) - (matrix[2][2] + bias[2])) * 25;
}

function estimateTint(matrix: [Vec3, Vec3, Vec3], bias: Vec3): number {
  return ((matrix[1][1] + bias[1]) - ((matrix[0][0] + bias[0] + matrix[2][2] + bias[2]) / 2)) * 25;
}

function createMatrix(rows: number, cols: number): number[][] {
  return Array.from({ length: rows }, () => Array.from({ length: cols }, () => 0));
}

function average(values: Vec3): number {
  return (values[0] + values[1] + values[2]) / 3;
}

function formatSigned(value: number): string {
  const rounded = Math.abs(value) >= 10 ? value.toFixed(0) : value.toFixed(2);
  return value > 0 ? `+${rounded}` : rounded;
}

function parseOptionalNumber(lines: string[], key: string, fallback: number): number {
  const value = readValue(lines, key);
  if (value == null) return fallback;
  const parsed = Number.parseFloat(value.replace(/^\+/, ""));
  if (!Number.isFinite(parsed)) {
    throw new Error(`Invalid numeric value for ${key}.`);
  }
  return parsed;
}

function readValue(lines: string[], key: string): string | null {
  const normalizedKey = normalizeKey(key);
  for (const line of lines) {
    const separatorIndex = findSeparatorIndex(line);
    if (separatorIndex < 0) continue;
    const lineKey = normalizeKey(line.slice(0, separatorIndex));
    if (lineKey === normalizedKey) {
      return line.slice(separatorIndex + 1).trim();
    }
  }
  return null;
}

function parseNumberList(value: string): number[] {
  return value
    .replace(/[，,]/g, " ")
    .split(/\s+/)
    .map((part) => Number.parseFloat(part.replace(/^\+/, "")))
    .filter(Number.isFinite);
}

function normalizeKey(value: string): string {
  return value
    .replace(/[：:]/g, "")
    .trim()
    .toLowerCase();
}

function findSeparatorIndex(value: string): number {
  const colon = value.indexOf(":");
  const wideColon = value.indexOf("：");
  if (colon < 0) return wideColon;
  if (wideColon < 0) return colon;
  return Math.min(colon, wideColon);
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}
